/**
 * audioSegmentation.ts
 * 
 * In-memory audio recording, Web Audio API decoding, and word-by-word segmentation.
 * Keeps all audio in memory (Blob / AudioBuffer), zero network uploads, zero cloud storage.
 */

export interface WordAudioSegment {
  word: string;
  index: number;
  startTime: number;
  endTime: number;
  duration: number;
}

export class InMemoryAudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private isRecordingInternal = false;

  get isRecording(): boolean {
    return this.isRecordingInternal;
  }

  async warmUp(): Promise<void> {
    // No-op: Do not pre-acquire microphone stream to avoid locking hardware out on mobile devices
  }

  async start(): Promise<void> {
    if (this.isRecordingInternal) return;
    this.chunks = [];

    try {
      // Reuse existing active stream if available, avoiding getUserMedia latency
      if (!this.stream || !this.stream.active) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Microphone access is not supported in this browser.');
        }

        this.stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });
      }

      // Pick best supported mimeType
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4'
      ];
      const supportedMime = mimeTypes.find(type => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) || '';

      const options = supportedMime ? { mimeType: supportedMime } : undefined;
      this.mediaRecorder = new MediaRecorder(this.stream, options);

      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) {
          this.chunks.push(e.data);
        }
      };

      this.mediaRecorder.start(100); // chunk every 100ms
      this.isRecordingInternal = true;
    } catch (err) {
      this.cleanup();
      throw err;
    }
  }

  async stop(): Promise<Blob | null> {
    if (!this.isRecordingInternal || !this.mediaRecorder) {
      this.cleanup();
      return null;
    }

    return new Promise<Blob | null>((resolve) => {
      const recorder = this.mediaRecorder;
      if (!recorder) {
        this.cleanup();
        resolve(null);
        return;
      }

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = this.chunks.length > 0 ? new Blob(this.chunks, { type: mimeType }) : null;
        this.cleanup();
        resolve(blob);
      };

      try {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      } catch (e) {
        console.warn('Error stopping MediaRecorder:', e);
        this.cleanup();
        resolve(null);
      }
    });
  }

  cancel(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        // ignore
      }
    }
    this.cleanup();
  }

  private cleanup(): void {
    this.isRecordingInternal = false;
    this.mediaRecorder = null;
    this.chunks = [];
    if (this.stream) {
      try {
        this.stream.getTracks().forEach(t => t.stop());
      } catch (e) {
        // ignore
      }
      this.stream = null;
    }
  }

  /**
   * Fully releases the audio stream and microphone hardware when the modal unmounts.
   */
  dispose(): void {
    this.cleanup();
    if (this.stream) {
      try {
        this.stream.getTracks().forEach(t => t.stop());
      } catch (e) {
        // ignore
      }
      this.stream = null;
    }
  }
}

/**
 * Shared singleton or factory AudioContext for decoding and segmentation.
 */
let sharedAudioContext: AudioContext | null = null;

export function getAudioContext(): AudioContext {
  if (typeof window === 'undefined') {
    throw new Error('AudioContext is only available in browser environments.');
  }
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    sharedAudioContext = new AudioCtx();
  }
  if (sharedAudioContext.state === 'suspended') {
    sharedAudioContext.resume().catch(() => {});
  }
  return sharedAudioContext;
}

/**
 * Decodes an in-memory audio Blob into an AudioBuffer.
 */
export async function decodeAudioBlob(blob: Blob): Promise<AudioBuffer> {
  const ctx = getAudioContext();
  const arrayBuffer = await blob.arrayBuffer();
  return await ctx.decodeAudioData(arrayBuffer);
}

/**
 * Estimates realistic relative duration weight of a word in spoken English.
 * Unstressed grammatical clitics ("the", "a", "of") are typically 100-180ms,
 * while multi-syllable content words ("mountains", "schedule") are 350-600ms.
 */
function estimateWordDurationWeight(word: string): number {
  const clean = word.toLowerCase().replace(/[^a-z0-9']/g, '');
  if (!clean) return 1.0;

  // Short grammatical function words in English are unstressed and spoken rapidly
  const unstressedWords = new Set([
    'a', 'an', 'the', 'to', 'of', 'in', 'on', 'at', 'by', 'for', 'with',
    'as', 'is', 'it', 'its', 'and', 'or', 'but', 'so', 'if', 'up', 'out'
  ]);
  if (unstressedWords.has(clean)) {
    return 1.0;
  }

  // Count vowel groups as syllable heuristic
  const syllables = Math.max(1, (clean.match(/[aeiouy]+/g) || []).length);
  const charFactor = Math.min(2.0, clean.length * 0.15);
  return Math.max(1.6, syllables * 1.3 + charFactor);
}

/**
 * Segments an AudioBuffer into word-level segments using energy envelopes and silence detection.
 */
export function segmentAudioByWords(audioBuffer: AudioBuffer, words: string[]): WordAudioSegment[] {
  const cleanWords = words.filter(w => w && w.trim().length > 0);
  if (cleanWords.length === 0) return [];

  const duration = audioBuffer.duration;
  if (cleanWords.length === 1 || duration <= 0.3) {
    return [{
      word: cleanWords[0],
      index: 0,
      startTime: 0,
      endTime: duration,
      duration: duration
    }];
  }

  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const frameSize = Math.floor(sampleRate * 0.025); // 25ms frames
  const frameCount = Math.floor(channelData.length / frameSize);

  // Calculate RMS energy per frame
  const energies: number[] = new Array(frameCount);
  let maxEnergy = 0;
  for (let f = 0; f < frameCount; f++) {
    let sumSquares = 0;
    const startSample = f * frameSize;
    for (let i = 0; i < frameSize; i++) {
      const val = channelData[startSample + i];
      sumSquares += val * val;
    }
    const rms = Math.sqrt(sumSquares / frameSize);
    energies[f] = rms;
    if (rms > maxEnergy) maxEnergy = rms;
  }

  // Smooth energy envelope using a 3-frame moving average to prevent noise glitch minima
  const smoothedEnergies: number[] = new Array(frameCount);
  for (let f = 0; f < frameCount; f++) {
    const prev = f > 0 ? energies[f - 1] : energies[f];
    const next = f < frameCount - 1 ? energies[f + 1] : energies[f];
    smoothedEnergies[f] = (prev + energies[f] + next) / 3;
  }

  // Find start and end of active speech
  const threshold = Math.max(0.01, maxEnergy * 0.12);
  let speechStartFrame = 0;
  while (speechStartFrame < frameCount && smoothedEnergies[speechStartFrame] < threshold) {
    speechStartFrame++;
  }

  let speechEndFrame = frameCount - 1;
  while (speechEndFrame > speechStartFrame && smoothedEnergies[speechEndFrame] < threshold) {
    speechEndFrame--;
  }

  // Add small speech margin (50ms)
  const paddingFrames = Math.floor(0.05 / 0.025);
  speechStartFrame = Math.max(0, speechStartFrame - paddingFrames);
  speechEndFrame = Math.min(frameCount - 1, speechEndFrame + paddingFrames);

  const speechStartTime = (speechStartFrame * frameSize) / sampleRate;
  const speechEndTime = (speechEndFrame * frameSize) / sampleRate;
  const activeDuration = Math.max(0.2, speechEndTime - speechStartTime);

  const n = cleanWords.length;
  const weights = cleanWords.map(estimateWordDurationWeight);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  let cumWeight = 0;
  const splitPoints: number[] = [speechStartTime];

  // For multi-word audio, search for local energy minima near expected weighted division points
  for (let k = 1; k < n; k++) {
    cumWeight += weights[k - 1];
    const targetTime = speechStartTime + (cumWeight / totalWeight) * activeDuration;
    const targetFrame = Math.floor((targetTime * sampleRate) / frameSize);

    // Search within +/- 200ms of weighted expected target
    const searchRadius = Math.floor(0.20 / 0.025);
    const minAllowedFrame = Math.floor((splitPoints[k - 1] * sampleRate) / frameSize) + Math.floor(0.08 / 0.025);
    const searchStart = Math.max(minAllowedFrame, targetFrame - searchRadius);
    const searchEnd = Math.min(speechEndFrame - Math.floor(0.08 / 0.025), targetFrame + searchRadius);

    let minVal = Infinity;
    let bestFrame = targetFrame;
    if (searchStart <= searchEnd) {
      for (let f = searchStart; f <= searchEnd; f++) {
        if (smoothedEnergies[f] < minVal) {
          minVal = smoothedEnergies[f];
          bestFrame = f;
        }
      }
    }

    const splitTime = (bestFrame * frameSize) / sampleRate;
    splitPoints.push(Math.max(splitPoints[k - 1] + 0.08, splitTime));
  }

  splitPoints.push(speechEndTime);

  // Construct segments ensuring strictly non-overlapping increasing bounds
  const segments: WordAudioSegment[] = [];
  for (let i = 0; i < n; i++) {
    const sStart = Math.max(0, splitPoints[i]);
    const sEnd = Math.min(duration, Math.max(sStart + 0.1, splitPoints[i + 1]));
    segments.push({
      word: cleanWords[i],
      index: i,
      startTime: sStart,
      endTime: sEnd,
      duration: Math.max(0.1, sEnd - sStart)
    });
  }

  return segments;
}

/**
 * Plays a specific segment of an AudioBuffer directly through Web Audio API.
 * Returns an abort / stop function.
 */
export function playBufferSegment(
  audioBuffer: AudioBuffer,
  startTime: number,
  duration: number,
  playbackRate: number = 1.0,
  onEnded?: () => void
): () => void {
  const ctx = getAudioContext();
  let source: AudioBufferSourceNode | null = ctx.createBufferSource();
  source.buffer = audioBuffer;
  source.playbackRate.value = playbackRate;
  source.connect(ctx.destination);

  let stopped = false;
  source.onended = () => {
    if (!stopped && onEnded) {
      onEnded();
    }
  };

  const safeStart = Math.max(0, startTime);
  const safeDuration = Math.min(duration, audioBuffer.duration - safeStart);

  source.start(0, safeStart, Math.max(0.05, safeDuration));

  return () => {
    stopped = true;
    if (source) {
      try {
        source.stop();
      } catch (e) {
        // already stopped
      }
      source.disconnect();
      source = null;
    }
  };
}

/**
 * Plays the full AudioBuffer with optional rate and callbacks.
 */
export function playFullAudioBuffer(
  audioBuffer: AudioBuffer,
  playbackRate: number = 1.0,
  onEnded?: () => void
): () => void {
  return playBufferSegment(audioBuffer, 0, audioBuffer.duration, playbackRate, onEnded);
}
